var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { ButtonSizeEnum } from '@inter-ikea-kompis/component-button';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import SheetFooterButtonsStyle from './SheetFooterButtonsStyle.js';
/**
 * SheetFooter component.
 *
 * @event buttonClick Click on buttons.
 */
export default class SheetFooterButtons extends KompisElement {
    constructor() {
        super(...arguments);
        // Primary button label.
        this.primaryLabel = null;
        // Secondary button label.
        this.secondaryLabel = null;
        // Compact placing buttons next to each other (in small sheet)
        this.compact = false;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
        this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const containerRect = this.getBoundingClientRect();
        return html `
			<style>
				${SheetFooterButtonsStyle.getStyle({
            theme: this.theme,
            hasSecondaryButton: !!this.secondaryLabel,
            compact: this.compact,
            container: this,
            containerRect: containerRect
        })}
			</style>
			<div class="container">
				${this.getPrimaryButton()} ${this.getDivider()} ${this.getSecondaryButton()}
			</div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        super.connectedCallback();
        this.resizeObserver.observe(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver.unobserve(this);
        this.breakpointObserver.disconnect();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        return true;
    }
    /**
     * @returns True if landscape layout.
     */
    getButtonSize() {
        const breakpointIndex = this.breakpointObserver.getBreakpointIndex();
        const breakpointIndexIsSmallOrNull = breakpointIndex === null || breakpointIndex <= ThemeBreakpointIndexEnum.small;
        if (breakpointIndexIsSmallOrNull && this.compact) {
            return ButtonSizeEnum.small;
        }
        return ButtonSizeEnum.medium;
    }
    getDivider() {
        if (!this.secondaryLabel || !this.primaryLabel) {
            return null;
        }
        return html ` <div class="divider"></div>`;
    }
    /**
     * @returns Primary button HTMl.
     */
    getPrimaryButton() {
        if (!this.primaryLabel) {
            return null;
        }
        const primaryVariant = 'primary';
        return html `
			<skapa-button
				class="primaryButton"
				fluid
				size="${this.getButtonSize() === ButtonSizeEnum.small ? 'small' : 'medium'}"
				variant="${primaryVariant}"
				@click="${() => this.dispatchEvent(new CustomEvent('buttonClick', { bubbles: true, detail: { type: 'primaryButton' } }))}"
			>
				${this.primaryLabel}
			</skapa-button>
		`;
    }
    /**
     * @returns Secondary button HTMl.
     */
    getSecondaryButton() {
        if (!this.secondaryLabel) {
            return null;
        }
        const secondaryVariant = 'secondary';
        return html `
			<skapa-button
				class="secondaryButton"
				fluid
				size="${this.getButtonSize() === ButtonSizeEnum.small ? 'small' : 'medium'}"
				variant="${secondaryVariant}"
				@click="${() => this.dispatchEvent(new CustomEvent('buttonClick', { bubbles: true, detail: { type: 'secondaryButton' } }))}"
			>
				${this.secondaryLabel}
			</skapa-button>
		`;
    }
}
__decorate([
    property({ type: Object, required: true })
], SheetFooterButtons.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], SheetFooterButtons.prototype, "primaryLabel", void 0);
__decorate([
    property({ type: String })
], SheetFooterButtons.prototype, "secondaryLabel", void 0);
__decorate([
    property({ type: Boolean })
], SheetFooterButtons.prototype, "compact", void 0);
//# sourceMappingURL=SheetFooterButtons.js.map